import { Component, ReactElement, ReactNode } from 'react'
import { RewardStatus, Reward, BountyAppState } from 'interfaces/bounty'
import { RewardsHistoryItemWithId } from 'components/features/rewards/rewardPage/store'
import Table2, { TableContextType, TableContext } from 'components/molecules/tables/Table2'
import { formatRelativeDate } from 'helpers/dateTimeHelpers'
import * as rowMobileStyles from 'styles/molecules/tables/CommonRowMobile.css'
import * as styles from 'components/features/pfm/RewardDetails/RewardDetailsRow.css'
import * as cn from 'classnames'
import TableNoWrapValue from 'components/atoms/tables/TableNoWrapValue'
import Progress from 'components/features/pfm/RewardDetails/Progress'
import StatusCopy from 'components/features/pfm/RewardDetails/StatusCopy'
import DismissButton from 'components/features/pfm/RewardDetails/DismissButton'
import withContext from 'hocs/withContext/withContext'
import { formatRewardAmount } from 'helpers/formatHelpers'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { CRYPTO_REWARDS_ORDER_STATUS_FULFILLED } from 'components/features/rewards/rewardsPane/constants/orders'
import { CryptoCurrencyNameById } from 'components/features/rewards/rewardsPane/containers/CryptoCurrencyNameById/CryptoCurrencyNameById'
import TextButton from 'components/atoms/buttons/TextButton'

type RewardDetailsRowProps = {
  table: TableContextType
  row: RewardsHistoryItemWithId
  isDismissing: boolean
  onProgressClick: (modalType: BountyAppState['reward']['modalType']) => void
  onDismissClick: () => void
  onOpenCryptoDetails: (cryptoOrderId: string) => void
}

type AcceptableRowFormat = {
  name: ReactNode
  isCrypto: boolean
  amount: string
  status: RewardStatus
  lastUpdated: string
  description: string
}

export const mapRowInAcceptableRewardDetailsRowFormat = (row: RewardsHistoryItemWithId): AcceptableRowFormat => {
  if (row.transaction_type === 'crypto') {
    const { ticker, status: cryptoTransactionStatus, amount_usd, created, filled, description, reward } = row
    return {
      name: <CryptoCurrencyNameById currencyId={ ticker } />,
      isCrypto: true,
      amount: formatUSCurrency(cryptoTransactionStatus === 'completed' ? reward : amount_usd, 'penny'),
      status: cryptoTransactionStatus === 'completed' ? 'cryptoConfirmed' :
        cryptoTransactionStatus === 'cancelled' ? 'cryptoCancelled' :
          'cryptoPending',
      lastUpdated: String(cryptoTransactionStatus === CRYPTO_REWARDS_ORDER_STATUS_FULFILLED
        ? filled
        : created),
      description,
    }
  } else {

    const { name, amount_ex, status, last_updated, description } = row
    return {
      name,
      isCrypto: false,
      amount: formatRewardAmount(amount_ex),
      status,
      lastUpdated: last_updated,
      description,
    }
  }

}

class RewardDetailsRow extends Component<RewardDetailsRowProps> {
  render () {
    const {
      row,
      isDismissing,
      onDismissClick,
    } = this.props
    const {
      name,
      isCrypto,
      amount,
      status,
      lastUpdated,
      description,
    } = mapRowInAcceptableRewardDetailsRowFormat(row)

    const rowKey = this.props.table.getRowKey(row)

    const descriptionTheme = (() => {
      if (row.transaction_type === 'crypto') {
        if (row.type === 'buy') {
          return 'buy'
        }
        if (row.type === 'sell') {
          return 'sell'
        }
      }

      return 'regular'
    })()

    return (
      <Table2.Row key={ rowKey }>
        <Table2.Cell rowKey={ rowKey } name='name'>
          <div className={ styles.name }>
            { name }
            { this.renderDescription(description, descriptionTheme) }
          </div>
        </Table2.Cell>
        <Table2.Cell rowKey={ rowKey } name='amount'>
          <div className={ styles.amount }>
            <TableNoWrapValue>{ amount }</TableNoWrapValue>
          </div>
        </Table2.Cell>
        <Table2.Cell rowKey={ rowKey } name='progress'>
          <Progress status={ status } onClick={ this.handleStatusDetails } />
        </Table2.Cell>
        <Table2.Cell rowKey={ rowKey } name='status'>
          <StatusCopy status={ status } onClick={ this.handleStatusDetails } />
        </Table2.Cell>
        <Table2.Cell rowKey={ rowKey } name='last_updated'>
          { formatRelativeDate(lastUpdated) }
        </Table2.Cell>
        <Table2.Cell rowKey={ rowKey } name='dismissed'>
          <div className={ styles.dismissButtonWrapper }>
            {isCrypto
              ? <TextButton size='regular' onClick={ this.handleOpenCryptoDetails }>Details</TextButton>
              : <DismissButton reward={ row as Reward } isLoading={ isDismissing } onClick={ onDismissClick } />}
          </div>
        </Table2.Cell>
      </Table2.Row>
    )
  }

  handleStatusDetails = () => {
    const { onProgressClick, row } = this.props
    onProgressClick(row.transaction_type === 'crypto' ? 'crypto' : 'cash')
  }

  handleOpenCryptoDetails = () => {
    const { row, onOpenCryptoDetails } = this.props
    onOpenCryptoDetails(row.uniqKey)
  }

  renderDescription (description: string, theme: 'regular' | 'buy' | 'sell'): ReactElement | null {
    return description ? (
      <div className={ cn(rowMobileStyles.subtitle, styles.description, styles[theme]) }>
        { description }
      </div>
    ) : null
  }
}

const t = withContext(TableContext, 'table')(RewardDetailsRow)
export default t
